import { Paperclip } from 'lucide-react';
import type { BugFilters, BugReport } from '../../lib/bugReportsApi';
import { PriorityBadge, StatusBadge, formatDateTime } from './bugVisuals';
import { useI18n } from '../../contexts/I18nContext';

type SortField = NonNullable<BugFilters['sort']>;

interface Props {
  reports: BugReport[];
  sort: SortField;
  order: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  onOpen: (report: BugReport) => void;
  filtered: boolean;
}

function SortHeader({
  label,
  field,
  sort,
  order,
  onSort,
}: {
  label: string;
  field: SortField;
  sort: SortField;
  order: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}) {
  const active = sort === field;
  return (
    <th className="px-3 py-2 text-left font-medium text-gray-600">
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 hover:text-gray-900"
      >
        {label}
        {active && <span aria-hidden>{order === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

export function BugReportsTable({ reports, sort, order, onSort, onOpen, filtered }: Props) {
  const { t } = useI18n();

  if (reports.length === 0) {
    return (
      <p className="py-8 text-center text-gray-500">
        {filtered ? t('bugReports.emptyFiltered') : t('bugReports.empty')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <SortHeader
              label={t('bugReports.column.identifier')}
              field="number"
              sort={sort}
              order={order}
              onSort={onSort}
            />
            <th className="px-3 py-2 text-left font-medium text-gray-600">
              {t('bugReports.column.title')}
            </th>
            <SortHeader
              label={t('bugReports.column.status')}
              field="status"
              sort={sort}
              order={order}
              onSort={onSort}
            />
            <SortHeader
              label={t('bugReports.column.priority')}
              field="priority"
              sort={sort}
              order={order}
              onSort={onSort}
            />
            <th className="px-3 py-2 text-left font-medium text-gray-600">
              {t('bugReports.column.reporter')}
            </th>
            <SortHeader
              label={t('bugReports.column.createdAt')}
              field="createdAt"
              sort={sort}
              order={order}
              onSort={onSort}
            />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {reports.map((report) => (
            <tr
              key={report.id}
              onClick={() => onOpen(report)}
              className="cursor-pointer hover:bg-gray-50"
            >
              <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-500">
                {report.identifier}
              </td>
              <td className="max-w-md px-3 py-2">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-gray-900">{report.title}</span>
                  {report.attachments.length > 0 && (
                    <span
                      className="inline-flex shrink-0 items-center gap-0.5 text-[11px] text-gray-400"
                      title={`${report.attachments.length}`}
                    >
                      <Paperclip size={11} />
                      {report.attachments.length}
                    </span>
                  )}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <StatusBadge status={report.status} />
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <PriorityBadge priority={report.priority} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                {report.reporter?.name ?? t('bugReports.unknownReporter')}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-500">
                {formatDateTime(report.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
